import { FC, useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import paymentStyle from "./PaymentStyle";
import { currencyFormat } from "../../utils/Utility";
import { useTranslation } from "react-i18next";
import oneinc from "../../assets/images/oneinc.png";
import Paypal from "../../assets/images/Paypal.jpg";
import stripe from "../../assets/images/stripe.jpg";
import wepay from "../../assets/images/wepay.jpg";
import { Card } from "@mui/material";
import Radio from "@mui/material/Radio";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import { useLocation } from "react-router-dom";

interface IProps {
  amount: string;
  carrierPolicyNumber: string;
}
interface payNowImages {
  src: string;
  title: string;
}

const payNowImageReferences = [
  { src: stripe, title: "stripe", label: "Stripe", iconBg: "#ffe6b3" },
  //  { src: Paypal, title: "paypal", label: "PayPal", iconBg: "#e6f0ff" },
  // { src: wepay, title: "wepay", label: "Wepay", iconBg: "#fffbe6" },
];

const unavailableGateways = [
  {
    title: "paypal",
    label: "PayPal",
    status: "Unavailable",
    src: Paypal,
    iconBg: "#e6f0ff",
  },
  {
    title: "wepay",
    label: "Wepay",
    status: "Unavailable",
    src: wepay,
    iconBg: "#fffbe6",
  },
  { title: "crypto", label: "Crypto", status: "Unavailable" },
  { title: "bank", label: "Bank Transfer", status: "Maintenance" },
];

const Payment: FC<IProps> = (props) => {
  const { amount, carrierPolicyNumber } = props;
  const [amountValue, setAmountValue] = useState<string>(amount);
  const [error, setError] = useState<string>("");
  const [selectedGateway, setSelectedGateway] = useState<string | null>(null);
  const location = useLocation();

  const { t } = useTranslation();

  // Track origin so success page can route back appropriately
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/customer/newcustomerDashboard")) {
      sessionStorage.setItem("payment_origin", "/customer/newcustomerDashboard");
    }
  }, [location.pathname]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const regex = /^\d{0,5}(\.\d{0,2})?$/;
    if (regex.test(value) || value === "") {
      setAmountValue(value);
      setError("");
    }
  };

  const getTotalAmount = useCallback(() => {
    const totalAmount: number = +parseFloat(amountValue || "0").toFixed(2);
    return totalAmount * 100;
  }, [amountValue]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = document.querySelector("form");
    if (!form || !(form instanceof HTMLFormElement)) {
      console.error("Form reference is invalid.");
      return;
    }
    const totalAmount = getTotalAmount();
    if (selectedGateway?.toLowerCase() === "stripe") {
      if (totalAmount / 100 < 0.5) {
        setError(t("Amount must be greater than $0.50."));
      } else {
        form.submit();
      }
    }
  };

  const handleGatewayChange = (paymentGateway: string) => {
    // Toggle off if clicking on already selected gateway
    if (selectedGateway === paymentGateway) {
      setSelectedGateway(null);
    } else {
      setSelectedGateway(paymentGateway);
    }
  };

  // --- Payment Information Card ---
  const getPaymentInfoCard = () => (
    <Box sx={paymentStyle.card}>
      <Typography sx={paymentStyle.paymentInfoTitle}>
        <Box component="span" sx={{ mr: 1, color: "#20a7db" }}>
          🧾
        </Box>
        {t("Payment Information")}
      </Typography>
      <Box sx={paymentStyle.paymentInfoRow}>
        <Typography sx={paymentStyle.paymentInfoLabel}>
          {t("Policy Number:")}
        </Typography>
        <Typography sx={paymentStyle.paymentInfoValue}>
          {carrierPolicyNumber}
        </Typography>
      </Box>
      <Box sx={paymentStyle.paymentInfoRow}>
        <Typography sx={paymentStyle.paymentInfoLabel}>{t("Amount Due:")}</Typography>
        <Typography sx={paymentStyle.paymentInfoAmount}>
          {currencyFormat("us", +parseFloat(amount || "0").toFixed(2))}
        </Typography>
      </Box>
    </Box>
  );

  // --- Review Card ---
  const getReviewCard = () => (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "10px",
        background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        padding: "1.5rem",
        marginTop: "-1.5rem",
        marginBottom: "1.5rem",
        width: "100%",
        maxWidth: 420,
      }}
    >
      <Typography sx={{ fontWeight: 600, mb: 2, fontFamily: "Noto Sans" }}>
        {t("Review Your Payment")}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontFamily: "Noto Sans" }}>Amount:</Typography>
        <Typography sx={{ fontWeight: 600, fontFamily: "Noto Sans" }}>
          {currencyFormat("us", +parseFloat(amountValue || "0").toFixed(2))}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontFamily: "Noto Sans" }}>{t("Payment Method:")}</Typography>
        <Typography sx={{ fontWeight: 600, fontFamily: "Noto Sans" }}>
          {selectedGateway
            ? payNowImageReferences.find((g) => g.title === selectedGateway)
                ?.label
            : ""}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontFamily: "Noto Sans" }}>{t("Policy Number:")}</Typography>
        <Typography sx={{ fontWeight: 600, fontFamily: "Noto Sans" }}>{carrierPolicyNumber}</Typography>
      </Box>
    </Box>
  );

  // --- Payment Gateways Card ---
  const getPaymentGatewaysCard = () => (
    <Box sx={paymentStyle.card}>
      <Typography sx={{ fontWeight: 600, mb: 2, fontFamily: "Noto Sans" }}>
        {t("Select Payment Gateway")}
      </Typography>
      <Typography sx={paymentStyle.sectionTitle}>{t("Available Options")}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
        {payNowImageReferences.map((item) => {
          const isSelected = selectedGateway === item.title;
          return (
            <Card
              key={item.title}
              sx={{
                ...paymentStyle.gatewayCard,
                border: isSelected ? "2px solid #20a7db" : "1px solid #e0e0e0",
                boxShadow: isSelected ? "0 0 0 2px #4f8cff33" : "none",
                background: "#fff",
                position: "relative",
                cursor: "pointer",
                transition: "border 0.2s, box-shadow 0.2s",
              }}
              onClick={() => handleGatewayChange(item.title)}
            >
              <Box sx={paymentStyle.gatewayCardLeft}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    background: item.iconBg,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <img
                    src={item.src}
                    alt={item.title}
                    style={{ width: 28, height: 28, objectFit: "contain" }}
                  />
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: "1.1rem", fontFamily: "Noto Sans" }}>
                  {item.label}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={t("Available")}
                  color="success"
                  size="small"
                  sx={paymentStyle.chip}
                />
                <Radio
                  checked={isSelected}
                  value={item.title}
                  name="paymentGateway"
                  sx={{
                    color: "#20a7db",
                    "&.Mui-checked": { color: "#20a7db" },
                  }}
                />
              </Box>
            </Card>
          );
        })}
      </Box>
      <Typography sx={paymentStyle.sectionTitle}>
        {t("Currently Unavailable")}
      </Typography>
      <Box sx={paymentStyle.unavailableGatewaysContainer}>
        {unavailableGateways.map((item) => (
          <Box
            key={item.title}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#f6f8fa",
              borderRadius: "12px",
              padding: "18px 20px",
              marginBottom: "8px",
              opacity: 0.7,
              border: "none",
              boxShadow: "none",
              height: "60px", // Fixed height for each item
              position: "relative",
              willChange: "auto", // Optimize for animations
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* Icon */}
              {item.src ? (
                <Box
                  sx={{
                    mr: 2,
                    fontSize: 28,
                    color: "#b0b7c3",
                    width: 28,
                    height: 28,
                    background: item.iconBg,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0, // Prevent icon from shrinking
                  }}
                >
                  <img
                    src={item.src}
                    alt={item.title}
                    style={{ 
                      width: 22, 
                      height: 22, 
                      objectFit: "contain",
                      display: "block" // Prevent layout shifts
                    }}
                  />
                </Box>
              ) : item.title === "crypto" ? (
                <Box sx={{ 
                  mr: 2, 
                  fontSize: 28, 
                  color: "#b0b7c3",
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  &#8383;
                </Box>
              ) : (
                <Box sx={{ 
                  mr: 2, 
                  fontSize: 28, 
                  color: "#b0b7c3",
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3"
                      y="10"
                      width="18"
                      height="8"
                      rx="2"
                      fill="#b0b7c3"
                    />
                    <rect
                      x="7"
                      y="6"
                      width="10"
                      height="4"
                      rx="1"
                      fill="#b0b7c3"
                    />
                  </svg>
                </Box>
              )}
              <Typography
                sx={{ 
                  color: "#b0b7c3", 
                  fontWeight: 600, 
                  fontSize: "1.1rem",
                  flex: 1,
                  minWidth: 0, // Allow text to shrink
                  fontFamily: "Noto Sans"
                }}
              >
                {item.label}
              </Typography>
            </Box>
            <Chip
              label={t(item.status)}
              size="small"
              sx={{
                background:
                  item.status === "Maintenance" ? "#fff4e5" : "#f0f2f5",
                color: item.status === "Maintenance" ? "#f5a623" : "#b0b7c3",
                fontWeight: 600,
                borderRadius: "8px",
                fontSize: "0.95rem",
                boxShadow: "none",
                flexShrink: 0, // Prevent chip from shrinking
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={paymentStyle.mainContainer}>
      {getPaymentInfoCard()}
      <form
        id="payment_form"
        data-test-id="test-payment_form"
        aria-label={t("Payment Form")}
        method="post"
        action={process.env.REACT_APP_PAYEMENT_URL}
        onSubmit={handleSubmit}
        style={{
          width: "100%",
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          gap: "1.25rem",
          minHeight: "auto",
          overflow: "visible",
        }}
      >
        <input
          type="hidden"
          name="carrierPolicyNo"
          value={carrierPolicyNumber}
        />
        <input
          type="hidden"
          name="totalAmount"
          value={getTotalAmount().toFixed(0)}
        />

        {getPaymentGatewaysCard()}

        {/* Action container with stable layout */}
        <Box sx={paymentStyle.actionContainer}>
          {/* Alert - shown when no gateway selected */}
          {!selectedGateway && (
            <Alert severity="warning" sx={{ mb: 2, fontFamily: "Noto Sans" }}>
              {t("Please select a payment gateway to proceed")}
            </Alert>
          )}

          {/* Review Card - shown when gateway selected */}
          {selectedGateway && (
            <Box sx={{ mb: 2 }}>
              {getReviewCard()}
            </Box>
          )}

          {/* Button - always visible but changes based on selection */}
          <Button
            id="payment_pay-now-button"
            data-test-id="test-payment_pay-now-button"
            aria-label={selectedGateway ? t("Pay Now") : t("Select Payment Method")}
            type={selectedGateway ? "submit" : "button"}
            name={selectedGateway ? "pay" : undefined}
            sx={{
              ...paymentStyle.payButton,
              background: selectedGateway 
                ? "linear-gradient(90deg, #20a7db 0%, #20a7db 100%)" 
                : "#20a7db",
              color: "#fff",
              fontWeight: selectedGateway ? 700 : 600,
              fontSize: selectedGateway ? "1.15rem" : "1.1rem",
              borderRadius: selectedGateway ? "10px" : "8px",
              mb: selectedGateway ? 0 : "16px",
              "&:hover": { background: "#20a7db" },
              transition: "all 0.3s ease",
            }}
            variant="contained"
            disabled={!selectedGateway}
          >
            {selectedGateway 
              ? `${t("Pay")} ${currencyFormat("us", +parseFloat(amountValue || "0").toFixed(2))} ${t("Now")}`
              : t("Select Payment Method")
            }
          </Button>

          {/* Security Footer - shown when gateway selected */}
          {selectedGateway && (
            <Box sx={paymentStyle.securityFooter}>
              <Box sx={paymentStyle.securityFooterIcon}>
                <span role="img" aria-label="lock">
                  🔒
                </span>{" "}
                {t("256-bit SSL. Secured by Stripe")}
              </Box>
              <span style={{ fontFamily: "Noto Sans" }}>
                {t("Your payment information is encrypted and secure. We do not store your payment details.")}
              </span>
            </Box>
          )}
        </Box>
      </form>
    </Box>
  );
};

export default Payment;
